// src/features/records/components/GalleryView/components/JustifiedGrid.js
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import MediaThumbnail from './MediaThumbnail';
import styles from './JustifiedGrid.module.css';
import { useJustifiedGrid } from '../hooks';
import {
  resolveJustifiedRowHeight,
  JUSTIFIED_GRID_GAP,
  JUSTIFIED_GRID_MIN_SCALE,
  JUSTIFIED_GRID_MAX_SCALE,
} from '../layout/config';

const JustifiedGrid = ({ media, loadMore, hasMore, loadingMore, onMediaClick }) => {
  const { t } = useTranslation();
  const { parentRef, containerWidth, rows, rowVirtualizer, gap } = useJustifiedGrid({
    items: media,
    gap: JUSTIFIED_GRID_GAP,
    rowHeightForWidth: resolveJustifiedRowHeight,
    minScale: JUSTIFIED_GRID_MIN_SCALE,
    maxScale: JUSTIFIED_GRID_MAX_SCALE,
    justifyLastRow: false,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalRows = rows.length;
  const lastVirtualRowIndex = virtualRows[virtualRows.length - 1]?.index ?? -1;

  useEffect(() => {
    if (!hasMore || loadingMore) return;
    if (!totalRows) return;
    if (lastVirtualRowIndex < 0) return;
    if (lastVirtualRowIndex >= totalRows - 1) {
      loadMore?.();
    }
  }, [lastVirtualRowIndex, totalRows, hasMore, loadingMore, loadMore]);

  return (
    <div ref={parentRef} className={styles.scrollParent}>
      <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
        {virtualRows.map((virtualRow) => {
          const row = rows[virtualRow.index];
          if (!row) return null;

          const isLast = virtualRow.index === totalRows - 1;
          const rowSpacing = isLast ? 0 : gap;
          const templateColumns = row.items.map((item) => `${item.width}px`).join(' ');

          return (
            <div
              key={row.key}
              ref={rowVirtualizer.measureElement}
              data-index={virtualRow.index}
              className={styles.rowWrapper}
              style={{
                transform: `translateY(${virtualRow.start}px)`,
                height: row.height + rowSpacing,
                paddingBottom: rowSpacing,
              }}
            >
              <div
                className={styles.row}
                style={{
                  height: row.height,
                  width: row.justify ? '100%' : Math.min(row.rowWidth, containerWidth || row.rowWidth),
                  gridTemplateColumns: templateColumns,
                  columnGap: gap,
                }}
              >
                {row.items.map((cell) => (
                  <MediaThumbnail
                    key={cell.key}
                    media={cell.item}
                    style={{ width: cell.width, height: row.height }}
                    onClick={onMediaClick}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {loadingMore && (
        <div className={styles.loadMoreTrigger}>
          <div className={styles.loadingMore}>
            <div className={styles.spinner} />
            <span>{t('records.views.gallery.loading_more')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

JustifiedGrid.propTypes = {
  media: PropTypes.arrayOf(PropTypes.object).isRequired,
  loadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  loadingMore: PropTypes.bool,
  onMediaClick: PropTypes.func,
};

export default JustifiedGrid;
